import { ChangeDetectionStrategy, Component, EventEmitter, Input } from "@angular/core";
import { DatasetListingService } from "./dataset-listing.service";
import { MatDialog } from "@angular/material/dialog";
import { TimeseriesPickerDialogComponent } from "../dialogs/timeseries-picker-dialog/timeseries-picker-dialog.component";
import { DatasetInfo } from "../dialogs/timeseries-picker-dialog/timeseries-picker-dialog.data";
import { LayerListService } from "../core/layer-list.service";
import { COVARIATE_DATASET_TYPE_ENUM } from "@models/mood/covariates/dataset";
import { LAYER_TYPE } from "../core/layers/base-layer.class";
import { AuthService } from "../core/auth/auth.service";
import { GroupedTreeNode, IDatasetFileTreeNode, IDatasetTreeNode, ITreeNode } from "../core/datasets/tree.interface";

@Component({
  selector: 'mood-tree-node',
  templateUrl: './covariate-node.component.html',
  styleUrls: ['./covariate-node.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CovariateTreeNodeComponent {
  @Input() node!: ITreeNode;
  @Input() showMenu!: boolean;
  @Input() entryIcon!: string;
  @Input() entryClicked?: EventEmitter<string>;

  private _expandDueToSearch: boolean = false;
  @Input() set expandDueToSearch(value: boolean) {
    this._expandDueToSearch = value;

    if (value && !this.isDatasetNode) {
      this.expanded = true;
    }
  }
  public get expandDueToSearch() {
    return this._expandDueToSearch;
  }

  expanded = false;

  constructor(private _dataListingService: DatasetListingService, private _dialog: MatDialog, private _layerListService: LayerListService, private _authService: AuthService) { }

  public get isTimeSeriesNode() {
    const datasetNode = this.node as IDatasetTreeNode;
    return datasetNode.original_dataset_type === COVARIATE_DATASET_TYPE_ENUM.TIMESERIES;
  }

  public get isFourierNode() {
    const datasetNode = this.node as IDatasetTreeNode;
    return datasetNode.original_dataset_type === COVARIATE_DATASET_TYPE_ENUM.FOURIER;
  }

  public get isDatasetNode() {
    return this.node.type === "DATASET";
  }

  public get isGroupedSubNode() {
    return this.isGroupedDatasetNode && (this.node as IDatasetFileTreeNode).filekey != null;
  }

  public get isGroupedDatasetNode() {
    return this.isDatasetNode && (this.node as IDatasetTreeNode).data_type === "GROUP";
  }

  public get isExpansionNode() {
    return this.node.type === "GROUP" || (this.isGroupedDatasetNode && !this.isGroupedSubNode); /// || ((this.node as IModelTreeNode).children);
  }

  public get groupNode() {
    return this.node as GroupedTreeNode;
  }


  // Click functions
  expandNode() {
    this.expanded = !this.expanded;
  }

  addTimeseriesClicked() {
    if (this.entryClicked) {
      this.entryClicked.emit((this.node as IDatasetTreeNode).dataset_id);
      return;
    }

    this._dialog.open(TimeseriesPickerDialogComponent, {
      data: {
        dataset_id: (this.node as IDatasetTreeNode).dataset_id
      } as DatasetInfo
    })
  }

  public get filteredChildren() {
    if (!this.isFourierNode || this._authService.userIsExpert) {
      return this.groupNode.children;
    } else {
      // TODO: I'd like to be able to use filenames here, but there are not all usable
      return this.groupNode.children?.filter(child => ["Minimum", "Maximum", "Mean"].includes(child.label));
    }
  }

  async addLayerToBrowser(node: ITreeNode) {
    const datasetNode = node as IDatasetTreeNode;

    if (this.entryClicked) {
      this.entryClicked.emit(datasetNode.dataset_id);
      return;
    }

    if (datasetNode.type === "DATASET") {
      if (datasetNode.file_type === LAYER_TYPE.RASTER) {
        const rasterDataset = await this._dataListingService.findDatasetById(datasetNode.dataset_id);

        const layer = datasetNode.data_type !== COVARIATE_DATASET_TYPE_ENUM.SINGLE ? {
          layer_id: (node as IDatasetFileTreeNode)?.filekey!,
          layer_name: (node as IDatasetFileTreeNode)?.label!
        } : undefined;

        this._layerListService.notifyRasterLayerAdded(rasterDataset!, layer);
      }
      else if (datasetNode.file_type === LAYER_TYPE.VECTOR) {
        this._layerListService.createDatabaseVectorLayer(datasetNode.label, datasetNode.dataset_id);
      }
    }
  }
}
